
import './OpenSatus_users.scss';
import './OpenSatus_users.mobile.scss';
import fnc from '../../mymod/commFunction';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import HomeViewCountBadge from '../../comm/HomeViewCountBadge';
import SetupDownloadCountBadge from '../../comm/SetupDownloadCountBadge';
import WorkerActiveCountBadge from '../../comm/WorkerActiveCountBadge';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import OpenSatusUsersMenu from './OpenSatusUsersMenu';

const BILL_FILTER = {
    ALL: 'all',
    MATHTAILER: 'mathtailer',
    KUESBLMS: 'kuesblms'
};

const EVENT_BILL_NOTE = '이벤트 지급';
const SMS_LIMIT_BYTES = 90;
const MESSAGE_PREFIX = '[매쓰테일러] ';
const CHARGE_SMS_SUFFIX = ' 사용내역확인요';

const MEMBER_FILTER_STORAGE_KEY = 'adminMemberListFilters';

function toNumberOrZero(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function getUsageRatePercent(item) {
    const chargedValue = toNumberOrZero(item.wallet_total_charged || item.total_charged);
    const usedValue = toNumberOrZero(item.wallet_total_used || item.total_used);

    if (chargedValue <= 0) return 0;

    return Math.round((usedValue / chargedValue) * 100);
}

function calculateEventCharge(amount) {
    const normalizedAmount = Math.max(0, parseInt(amount, 10) || 0);

    if (normalizedAmount < 50000) {
        return Math.round(normalizedAmount * 0.1);
    }

    if (normalizedAmount < 100000) {
        return Math.round(normalizedAmount * 0.2);
    }

    return Math.round(normalizedAmount * 0.3);
}

function normalizePhone(value) {
    const raw = String(value || '').trim();
    if (!raw || raw.toUpperCase() === 'NULL') return '';
    return raw.replace(/[^0-9]/g, '');
}

function prependMessagePrefix(message) {
    const text = String(message || '').trim();
    if (!text) return '';
    if (text.startsWith(MESSAGE_PREFIX) || text.startsWith('[매쓰테일러]')) return text;
    return `${MESSAGE_PREFIX}${text}`;
}

function getSmsBytes(message) {
    return new TextEncoder().encode(String(message || '')).length;
}

function trimTextToByteLimit(text, maxBytes) {
    if (maxBytes <= 0) return '';

    let result = '';
    for (const char of String(text || '')) {
        if (getSmsBytes(result + char) > maxBytes) break;
        result += char;
    }

    return result;
}

function buildChargeSmsMessage(amount, eventAmount) {
    const normalizedAmount = Math.max(0, parseInt(amount, 10) || 0);
    const normalizedEventAmount = Math.max(0, parseInt(eventAmount, 10) || 0);

    if (normalizedAmount <= 0 && normalizedEventAmount <= 0) {
        return '';
    }

    const messageCandidates = normalizedEventAmount > 0
        ? [
            `${normalizedAmount.toLocaleString()}원+이벤트(${normalizedEventAmount.toLocaleString()}원) 충전해드렸습니다.${CHARGE_SMS_SUFFIX}`,
            `${normalizedAmount.toLocaleString()}원+이벤트(${normalizedEventAmount.toLocaleString()}원) 충전.${CHARGE_SMS_SUFFIX}`
        ]
        : [
            `${normalizedAmount.toLocaleString()}원 충전해드렸습니다.${CHARGE_SMS_SUFFIX}`,
            `${normalizedAmount.toLocaleString()}원 충전.${CHARGE_SMS_SUFFIX}`
        ];

    const availableBytes = Math.max(0, SMS_LIMIT_BYTES - getSmsBytes(MESSAGE_PREFIX));

    for (const candidate of messageCandidates) {
        if (getSmsBytes(candidate) <= availableBytes) {
            return candidate;
        }
    }

    const suffixBytes = getSmsBytes(CHARGE_SMS_SUFFIX);
    const baseBudget = Math.max(0, availableBytes - suffixBytes);
    const fallbackBase = normalizedEventAmount > 0
        ? `${normalizedAmount.toLocaleString()}원+이벤트(${normalizedEventAmount.toLocaleString()}원) 충전`
        : `${normalizedAmount.toLocaleString()}원 충전`;
    const trimmedBase = trimTextToByteLimit(fallbackBase, baseBudget).trim();

    return `${trimmedBase}${CHARGE_SMS_SUFFIX}`.trim();
}

function getStoredMemberFilters() {
    if (typeof window === 'undefined') {
        return {
            billFilter: BILL_FILTER.MATHTAILER,
            keyword: ''
        };
    }

    try {
        const raw = window.sessionStorage.getItem(MEMBER_FILTER_STORAGE_KEY);
        if (!raw) {
            return {
                billFilter: BILL_FILTER.MATHTAILER,
                keyword: ''
            };
        }

        const parsed = JSON.parse(raw);
        return {
            billFilter: normalizeBillId(parsed?.billFilter),
            keyword: String(parsed?.keyword || '')
        };
    } catch (error) {
        return {
            billFilter: BILL_FILTER.MATHTAILER,
            keyword: ''
        };
    }
}

function normalizeBillId(value) {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return BILL_FILTER.MATHTAILER;
    const compact = normalized.replace(/[^a-z0-9]/g, '');
    if (compact === 'kuesblms') return BILL_FILTER.KUESBLMS;
    if (compact === 'mathtailer') return BILL_FILTER.MATHTAILER;
    return normalized;
}


const OpenSatus_users = () => {
    const storedFilters = getStoredMemberFilters();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const svrUrl = process.env.REACT_APP_SERVER_URL;
    const [list, setList] = useState([]);
    const [isListLoading, setIsListLoading] = useState(false);
    const [selectedSno, setSelectedSno] = useState(null);
    const [useyn, setUseyn] = useState(null);
    const [isAiVertex, setIsAiVertex] = useState('');
    const [currentBalance, setCurrentBalance] = useState(0);
    const [callPrice, setCallPrice] = useState(3000);
    const [eventChargePrice, setEventChargePrice] = useState(calculateEventCharge(3000));
    const [maxPage, setMaxPage] = useState(0);
    const [userMemo, setUserMemo] = useState('');
    const [billNoteOptions, setBillNoteOptions] = useState([]);
    const [selectedBillNote, setSelectedBillNote] = useState('');
    const [selectedEventBillNote, setSelectedEventBillNote] = useState(EVENT_BILL_NOTE);
    const [chargeSmsMessage, setChargeSmsMessage] = useState('');
    const [orgName, setOrgName] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [selectedName, setSelectedName] = useState('');
    const [selectedPhone, setSelectedPhone] = useState('');
    const [selectedBillId, setSelectedBillId] = useState(BILL_FILTER.MATHTAILER);
    const [selectedStCode, setSelectedStCode] = useState(null);
    const [isRefresh, setIsRefresh] = useState(false);
    const [sortKey, setSortKey] = useState('sDate');
    const [sortDir, setSortDir] = useState('desc');
    const [billFilter, setBillFilter] = useState(storedFilters.billFilter);
    const [keyword, setKeyword] = useState(storedFilters.keyword);

        


    useEffect(() => {
        fncSelect_listUsers();
        fncSelect_billNotes();
    }, [isRefresh]);

    useEffect(() => {
        document.body.style.cursor = isListLoading ? 'wait' : '';
        return () => {
            document.body.style.cursor = '';
        };
    }, [isListLoading]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        window.sessionStorage.setItem(MEMBER_FILTER_STORAGE_KEY, JSON.stringify({
            billFilter,
            keyword
        }));
    }, [billFilter, keyword]);


    
    function fncSelect_listUsers() {
        setIsListLoading(true);

        fnc.sendAxios('/userRoutes/selectUsers', {  }, (res) => {
            if (res && Array.isArray(res)) {
            const newList = getRecords(res);
            setList(newList);
            // console.log(res, 'res=================');
            } else {
            alert('검색에 실패했습니다.');
            }
            setIsListLoading(false);
        });
                
        function getRecords(res) {
            return res.map(item => {
                return {
                    sno: item["sno"],
                    id: item["id"],
                    userId: item["userId"],
                    stCode: item["stCode"],
                    stName: item["stName"],
                    hp: item["hp"],
                    singupDevice: item["singup_device"],
                    appVersion: item["app_version"],
                    sDate: item["sDate"],
                    userKind: item["userKind"],
                    eDate: item["eDate"],
                    useyn: item["useyn"],
                    isAiVertex: item["isAI_vertex"],
                    isAiKey: item["isAiKey"],
                    memo: item["memo"],
                    mathpixMaxPage: item["mathpix_maxpage"],
                    totalCharged: item["total_charged"],
                    totalPayment: item["total_payment"],
                    totalUsed: item["total_used"],
                    balance: item["balance"],
                    walletTotalCharged: item["wallet_total_charged"],
                    walletTotalPayment: item["wallet_total_payment"],
                    walletTotalUsed: item["wallet_total_used"],
                    walletBalance: item["wallet_balance"],
                    usageRate: getUsageRatePercent(item),
                    academyName: item["academyName"],
                    billId: normalizeBillId(item["bill_id"] || item["billId"]),

                };
            });
        }
    }

    function fncSelect_billNotes() {
        fnc.sendAxios('/userRoutes/selectBillNotes', {}, (res) => {
            if (res && Array.isArray(res)) {
                const options = res
                    .map(item => ({
                        value: item.note || '',
                        label: item.note || ''
                    }))
                    .filter(item => item.value !== '');

                setBillNoteOptions(options);
                if (!selectedBillNote && options.length > 0) {
                    setSelectedBillNote(options[0].value);
                }
            } else {
                setBillNoteOptions([]);
            }
        });
    }

    function handleUseyn(useyn) {
        if (!selectedSno) {
            alert('학원을 선택해주세요.');
            return;
        }

        const rtn = window.confirm(`id: ${selectedId}\n사용여부를 변경하시겠습니까?`);
        if (!rtn) return;

        fnc.sendAxios('/userRoutes/updateUseyn', { id: selectedId, useyn }, (res) => {
            if (res && res.success) {
            fncSelect_listUsers();
            setUseyn(useyn);
            } else {
            alert('변경에 실패했습니다.');
            }
        });
    }


    function onClickTr(item, index) {
        setSelectedSno(item.sno);
        setSelectedId(item.id);
        setSelectedName(item.stName || '');
        setSelectedPhone(item.hp || '');
        setSelectedBillId(item.billId || BILL_FILTER.MATHTAILER);
        setUseyn(item.useyn);
        setIsAiVertex(item.isAiVertex || '');
        setSelectedStCode(item.stCode);
        setMaxPage(parseInt(item.mathpixMaxPage) || 0);
        setUserMemo(item.memo || '');
    }

    function handleIsAiVertex(nextValue) {
        if (!selectedSno) {
            alert('학원을 선택해주세요.');
            return;
        }

        const normalized = String(nextValue || '').trim().toUpperCase() === 'Y' ? 'Y' : '';
        const rtn = window.confirm(`id: ${selectedId}\nAI사용 값을 변경하시겠습니까?`);
        if (!rtn) return;

        fnc.sendAxios('/userRoutes/updateIsAiVertex', { id: selectedId, isAI_vertex: normalized }, (res) => {
            if (res && res.success) {
                fncSelect_listUsers();
                setIsAiVertex(normalized);
            } else {
                alert('변경에 실패했습니다.');
            }
        });
    }

    function handleCallPriceChange(e) {
        const price = parseInt((e.target.value || '').replace(/,/g, '')) || 0;
        setCallPrice(price);
        setEventChargePrice(calculateEventCharge(price));
    }

    function handleWalletCharge({ amount, note, title }) {
        if (!selectedSno) {
            alert('학원을 선택해주세요.');
            return;
        }

        const normalizedAmount = parseInt(amount, 10) || 0;
        if (normalizedAmount === 0) {
            alert('금액을 입력하세요');
            return;
        }

        const rtn = window.confirm(`id: ${selectedId}\n이름: ${selectedName}\n${title} ${normalizedAmount.toLocaleString()}을 반영하시겠습니까?`);
        if (!rtn) return;

        fnc.sendAxios('/userRoutes/sp_wallet_charge', {
            user_id: selectedId,
            stCode: selectedStCode,
            charge_amount: normalizedAmount,
            charge_price: normalizedAmount,
            note: note || ''
        }, (res) => {
            if (res && res.success) {
                fncSelect_listUsers();
            } else {
                alert('변경에 실패했습니다.');
            }
        });
    }

    function handleSaveAiCharge() {
        handleWalletCharge({
            amount: callPrice,
            note: selectedBillNote,
            title: 'AI 충전금액'
        });
    }

    function handleSaveEventCharge() {
        handleWalletCharge({
            amount: eventChargePrice,
            note: selectedEventBillNote || EVENT_BILL_NOTE,
            title: '이벤트 충전금액'
        });
    }

    function handleApplyChargeSmsTemplate() {
        const template = buildChargeSmsMessage(callPrice, eventChargePrice);

        if (!template) {
            alert('충전 금액 또는 이벤트 금액을 먼저 입력하세요.');
            return;
        }

        setChargeSmsMessage(template);
    }

    function handleSendChargeSms() {
        if (!selectedSno) {
            alert('학원을 선택해주세요.');
            return;
        }

        const message = String(chargeSmsMessage || '').trim();
        if (!message) {
            alert('문자 내용을 입력하세요.');
            return;
        }

        const phone = normalizePhone(selectedPhone);
        if (phone.length < 10) {
            alert('문자 수신번호가 없습니다.');
            return;
        }

        const ok = window.confirm(
            [
                '단문 문자전송 하시겠습니까?',
                `이름: ${selectedName || '-'}`,
                `번호: ${phone}`,
                `내용: ${prependMessagePrefix(message)}`
            ].join('\n')
        );
        if (!ok) return;

        fnc.sendAxios('/smsRoutes/sendSms', {
            mode: 1,
            sendDate: '',
            toPhone: phone,
            personName: selectedName || '',
            studentNo: selectedStCode || '',
            billId: selectedBillId || BILL_FILTER.MATHTAILER,
            message: prependMessagePrefix(message)
        }, (res) => {
            if (res && res.success && Number(res.insertedCount || 0) > 0) {
                alert('문자전송이 등록되었습니다.');
            } else {
                alert(res?.message || '문자전송에 실패했습니다.');
            }
        });
    }

    function handleRefresh() {
        setIsRefresh(prev => !prev);
    }

    function handleUpdateMaxPage() {
        if (!selectedSno) {
            alert('학원을 선택해주세요.');
            return;
        }

        const page = parseInt(maxPage) || 0;
        const rtn = window.confirm(`id: ${selectedId}\n이름: ${selectedName}\nmaxPage를 ${page}로 수정하시겠습니까?`);
        if (!rtn) return;

        fnc.sendAxios('/userRoutes/updateMathpixMaxpage', {
            id: selectedId,
            stCode: selectedStCode,
            mathpix_maxpage: page
        }, (res) => {
            if (res && res.success) {
                fncSelect_listUsers();
            } else {
                alert('변경에 실패했습니다.');
            }
        });
    }

    function handleSaveUserMemo() {
        if (!selectedSno) {
            alert('학원을 선택해주세요.');
            return;
        }

        const rtn = window.confirm(`id: ${selectedId}\n이름: ${selectedName}\n회원 메모를 저장하시겠습니까?`);
        if (!rtn) return;

        fnc.sendAxios('/userRoutes/updateUserMemo', {
            id: selectedId,
            stCode: selectedStCode,
            memo: userMemo || ''
        }, (res) => {
            if (res && res.success) {
                fncSelect_listUsers();
                alert('메모를 저장했습니다.');
            } else {
                alert('변경에 실패했습니다.');
            }
        });
    }

    function handleSort(key) {
        if (sortKey === key) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    }

    function parseDateValue(value) {
        if (value === null || value === undefined || value === '') return null;
        if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
        if (typeof value === 'number') {
            const dateFromNumber = new Date(value);
            return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber;
        }

        const raw = String(value).trim();
        if (!raw) return null;

        const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    function getDateSortValue(value) {
        const parsed = parseDateValue(value);
        return parsed ? parsed.getTime() : Number.NEGATIVE_INFINITY;
    }

    function formatDateTimeForDisplay(value) {
        if (value === null || value === undefined || value === '') {
            return { date: '', time: '' };
        }

        const raw = String(value).trim();
        const rawMatch = raw.match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}(?::\d{2})?))?/);
        if (rawMatch) {
            return {
                date: rawMatch[1] || '',
                time: rawMatch[2] || ''
            };
        }

        const parsed = parseDateValue(value);
        if (!parsed) return { date: raw, time: '' };

        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        const hour = String(parsed.getHours()).padStart(2, '0');
        const minute = String(parsed.getMinutes()).padStart(2, '0');
        const second = String(parsed.getSeconds()).padStart(2, '0');

        return {
            date: `${year}-${month}-${day}`,
            time: `${hour}:${minute}:${second}`
        };
    }

    const filteredList = useMemo(() => {
        const normalizedKeyword = String(keyword || '').trim().toLowerCase();
        const phoneKeyword = /^[0-9\s()+-]+$/.test(normalizedKeyword)
            ? normalizePhone(normalizedKeyword)
            : '';

        return list.filter((item) => {
            const itemBillId = normalizeBillId(item.billId);
            const isBillMatched =
                billFilter === BILL_FILTER.ALL
                || itemBillId === billFilter;

            if (!isBillMatched) return false;

            if (!normalizedKeyword) return true;

            const idText = String(item.userId || '').toLowerCase();
            const nameText = String(item.stName || '').toLowerCase();
            const isPhoneMatched = phoneKeyword !== ''
                && normalizePhone(item.hp).includes(phoneKeyword);

            return idText.includes(normalizedKeyword)
                || nameText.includes(normalizedKeyword)
                || isPhoneMatched;
        });
    }, [list, billFilter, keyword]);

    const eventBillNoteOptions = useMemo(() => {
        if (billNoteOptions.some(item => item.value === EVENT_BILL_NOTE)) {
            return billNoteOptions;
        }

        return [{ value: EVENT_BILL_NOTE, label: EVENT_BILL_NOTE }, ...billNoteOptions];
    }, [billNoteOptions]);

    const sortedList = [...filteredList].sort((a, b) => {
        if (!sortKey) return 0;

        if (sortKey === 'sDate' || sortKey === 'eDate') {
            const ta = getDateSortValue(a[sortKey]);
            const tb = getDateSortValue(b[sortKey]);
            return sortDir === 'asc' ? ta - tb : tb - ta;
        }

        const va = a[sortKey] ?? '';
        const vb = b[sortKey] ?? '';
        const isNum = typeof va === 'number' || (va !== '' && !isNaN(Number(va)));
        if (isNum) {
            return sortDir === 'asc' ? Number(va) - Number(vb) : Number(vb) - Number(va);
        }
        const sa = va.toString().toLowerCase();
        const sb = vb.toString().toLowerCase();
        if (sa < sb) return sortDir === 'asc' ? -1 : 1;
        if (sa > sb) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const activeUserCount = filteredList.filter(
        (item) => String(item.useyn || '').trim().toUpperCase() === 'Y'
    ).length;
    const withdrawnUserCount = filteredList.filter(
        (item) => String(item.stName || '').trim() === '탈퇴회원'
    ).length;

    const sortIcon = (key) => {
        if (sortKey !== key) return <FaSort className='sort-icon inactive' />;
        return sortDir === 'asc' ? <FaSortUp className='sort-icon active' /> : <FaSortDown className='sort-icon active' />;
    };

    const SortTh = ({ label, sortkey }) => (
        <th className='th-sort' onClick={() => handleSort(sortkey)}>
            {label}{sortIcon(sortkey)}
        </th>
    );

    const formatNumber = (value) => Number(value || 0).toLocaleString();

    const renderDualMetric = (primaryValue, secondaryValue) => (
        <div className='metric-cell'>
            <span className='metric-main'>{formatNumber(secondaryValue)}</span>
            <span className='metric-sub'>{formatNumber(primaryValue)}</span>
        </div>
    );

    const chargeSmsBytes = useMemo(() => getSmsBytes(chargeSmsMessage), [chargeSmsMessage]);
    const isChargeSmsOverLimit = chargeSmsBytes > SMS_LIMIT_BYTES;

    //=========================================
    return (
    <div className='open-status-scroll'>
        
        <div className='OpenSatus_users'>
        <div className='member-content-shell'>
            <div className='member-filter-row'>
                <div className='bill-filter-buttons'>
                    <button
                        type='button'
                        className={billFilter === BILL_FILTER.ALL ? 'active' : ''}
                        onClick={() => setBillFilter(BILL_FILTER.ALL)}
                    >
                        전체
                    </button>
                    <button
                        type='button'
                        className={billFilter === BILL_FILTER.MATHTAILER ? 'active' : ''}
                        onClick={() => setBillFilter(BILL_FILTER.MATHTAILER)}
                    >
                        mathtailer
                    </button>
                    <button
                        type='button'
                        className={billFilter === BILL_FILTER.KUESBLMS ? 'active' : ''}
                        onClick={() => setBillFilter(BILL_FILTER.KUESBLMS)}
                    >
                        kuesbLms
                    </button>
                </div>

                <div className='member-search-box'>
                    <input
                        type='text'
                        value={keyword}
                        placeholder='아이디 / 회원이름 / 휴대폰'
                        aria-label='아이디 / 회원이름 / 휴대폰 검색'
                        onChange={(e) => setKeyword(e.target.value)}
                    />
                    <button
                        type='button'
                        className='btn-reset'
                        onClick={() => {
                            setBillFilter(BILL_FILTER.MATHTAILER);
                            setKeyword('');
                        }}
                    >
                        초기화
                    </button>
                    <button
                        type='button'
                        className={`btn-refresh ${isListLoading ? 'is-loading' : ''}`}
                        onClick={handleRefresh}
                        disabled={isListLoading}
                        aria-label={isListLoading ? '새로고침 중' : '새로고침'}
                    >
                        새로고침
                    </button>
                    <button
                        type='button'
                        className='btn-member-log'
                        onClick={() => navigate('/admin/login-history')}
                    >
                        회원로그
                    </button>
                </div>
                <div className='title-row'>
                    <div className='title-stats'>
                        <WorkerActiveCountBadge />
                        <HomeViewCountBadge />
                        <SetupDownloadCountBadge />
                    </div>
                </div>
            </div>

            <section className='sec-main'>
            <table className="table-hong table-main">
                <thead>
                <tr>
                    <th title={`사용회원 ${activeUserCount}명 / 탈퇴회원 ${withdrawnUserCount}명`}>
                        {activeUserCount} ({withdrawnUserCount})
                    </th>

                    <SortTh label='시작일' sortkey='sDate' />
                    <SortTh label='ID' sortkey='userId' />
                    <SortTh label='이름' sortkey='stName' />
                    <SortTh label='휴대폰' sortkey='hp' />
                    <SortTh label='가입기기' sortkey='singupDevice' />
                    <SortTh label='사용여부' sortkey='useyn' />
                    <SortTh label='AI사용' sortkey='isAiVertex' />
                    <SortTh label='API KEY' sortkey='isAiKey' />
                    <SortTh label='결제금액' sortkey='totalPayment' />
                    <SortTh label='총 충전 콜' sortkey='totalCharged' />
                    <SortTh label='총 사용 콜' sortkey='totalUsed' />
                    <SortTh label='잔여콜' sortkey='balance' />
                    <SortTh label='사용률' sortkey='usageRate' />
                    <SortTh label='maxpg' sortkey='mathpixMaxPage' />
                    <SortTh label='메모' sortkey='memo' />
                    <SortTh label='학원명' sortkey='academyName' />
            
                </tr>
                </thead>
                <tbody>
                {sortedList && sortedList.map((item, index) => (
                    <tr key={index} onClick={() => onClickTr(item, index)}
                    className={selectedSno === item.sno ? 'selected' : ''}>
                    <td>{index+1}</td>
                    <td>
                        <span>{formatDateTimeForDisplay(item.sDate).date}</span>
                        <br />
                        <span className='date-time-muted'>{formatDateTimeForDisplay(item.sDate).time}</span>
                    </td>
                    <td>
                        <span className='user-id'>{item.userId}</span>
                        <br/>
                        <span className='user-stcode'>{item.stCode}</span>
                    </td>
                    <td>{item.stName}</td>
                    <td>{item.hp}</td>
                    <td>
                        <span>{item.singupDevice}</span>
                        {item.appVersion && (
                            <span className='app-version'>{`v${item.appVersion}`}</span>
                        )}
                    </td>

                    <td>{item.useyn}</td>
                    <td>{item.isAiVertex}</td>
                    <td>{item.isAiKey}</td>
                    <td>{renderDualMetric(item.totalPayment, item.walletTotalPayment)}</td>
                    <td>{renderDualMetric(item.totalCharged, item.walletTotalCharged)}</td>
                    <td>{renderDualMetric(item.totalUsed, item.walletTotalUsed)}</td>
                    <td>{renderDualMetric(item.balance, item.walletBalance)}</td>
                    <td>{formatNumber(item.usageRate)}%</td>
                    <td>{formatNumber(item.mathpixMaxPage)}</td>
                    <td>{String(item.memo || '').trim() ? 'Y' : ''}</td>
                    <td>{item.academyName}</td>
                
                    </tr>
                ))}
                </tbody>

            </table>
            </section>
        </div>
        <section className='sec-menu'>
            <OpenSatusUsersMenu
                userMemo={userMemo}
                onUserMemoChange={(e) => setUserMemo(e.target.value)}
                useyn={useyn}
                isAiVertex={isAiVertex}
                onUse={() => handleUseyn('Y')}
                onStop={() => handleUseyn('N')}
                onEnableAi={() => handleIsAiVertex('Y')}
                onDisableAi={() => handleIsAiVertex('')}
                onSaveMemo={handleSaveUserMemo}
                maxPage={maxPage}
                onMaxPageChange={(e) => { const page = parseInt((e.target.value || '').replace(/,/g, '')) || 0; setMaxPage(page); }}
                onSaveMaxPage={handleUpdateMaxPage}
                callPrice={callPrice}
                onCallPriceChange={handleCallPriceChange}
                selectedBillNote={selectedBillNote}
                onSelectedBillNoteChange={(e) => setSelectedBillNote(e.target.value)}
                billNoteOptions={billNoteOptions}
                onSaveAiCharge={handleSaveAiCharge}
                eventChargePrice={eventChargePrice}
                onEventChargeChange={(e) => { const price = parseInt((e.target.value || '').replace(/,/g, '')) || 0; setEventChargePrice(price); }}
                selectedEventBillNote={selectedEventBillNote}
                onSelectedEventBillNoteChange={(e) => setSelectedEventBillNote(e.target.value)}
                eventBillNoteOptions={eventBillNoteOptions}
                onSaveEventCharge={handleSaveEventCharge}
                chargeSmsMessage={chargeSmsMessage}
                onChargeSmsChange={(e) => setChargeSmsMessage(e.target.value)}
                isChargeSmsOverLimit={isChargeSmsOverLimit}
                onApplyChargeSmsTemplate={handleApplyChargeSmsTemplate}
                onSendChargeSms={handleSendChargeSms}
            />
        </section>
        </div>
    </div>
    )};

    export default OpenSatus_users;
